import { apiClient } from '@/shared/api/client';
import type {
  Company,
  CreateCompanyRequest,
  UpdateCompanyRequest,
  UpdateSecuritySettingsRequest,
  UpdateNotificationPreferencesRequest,
  UpdateRefereeSettingsRequest,
  ListCompaniesParams,
} from '../types/company.types';
import type { ApiResponse } from '@/shared/types/api.types';

async function putCompany(path: string, body: unknown): Promise<Company> {
  const response = await apiClient<ApiResponse<Company>>(path, { method: 'PUT', body });
  return response.data;
}

async function patchCompany(path: string): Promise<Company> {
  const response = await apiClient<ApiResponse<Company>>(path, { method: 'PATCH' });
  return response.data;
}

export const companyService = {
  async listCompanies(params?: ListCompaniesParams): Promise<Company[]> {
    const queryParams = new URLSearchParams();
    if (params?.activeOnly) queryParams.set('activeOnly', 'true');
    const queryString = queryParams.toString();
    const endpoint = `/companies${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient<ApiResponse<Company[]>>(endpoint, { method: 'GET' });
    return response.data;
  },

  async getCompanyById(id: string): Promise<Company> {
    const response = await apiClient<ApiResponse<Company>>(`/companies/${id}`, { method: 'GET' });
    return response.data;
  },

  async createCompany(request: CreateCompanyRequest): Promise<Company> {
    const response = await apiClient<ApiResponse<Company>>('/companies', {
      method: 'POST',
      body: request,
    });
    return response.data;
  },

  updateCompany: (id: string, request: UpdateCompanyRequest): Promise<Company> =>
    putCompany(`/companies/${id}`, request),
  activateCompany: (id: string): Promise<Company> => patchCompany(`/companies/${id}/activate`),
  suspendCompany: (id: string): Promise<Company> => patchCompany(`/companies/${id}/suspend`),
  updateSecuritySettings: (id: string, settings: UpdateSecuritySettingsRequest): Promise<Company> =>
    putCompany(`/companies/${id}/security-settings`, settings),
  updateNotificationPreferences: (
    id: string,
    prefs: UpdateNotificationPreferencesRequest
  ): Promise<Company> => putCompany(`/companies/${id}/notification-preferences`, prefs),
  updateRefereeSettings: (id: string, settings: UpdateRefereeSettingsRequest): Promise<Company> =>
    putCompany(`/companies/${id}/referee-settings`, settings),
};
